import React, { useState } from "react";
import { useParams } from "react-router-dom";
import AnimatedPageContainer from "../../assets/containers/AnimatedPageContainer";
import Loading from "../../assets/containers/loadingDialog";
import NoResultAlter from "../../assets/components/UI/NoResultAlter";
import { baseURL } from "../../assets/constants/baseUrl";
import formStyles from "../Forms/styles/style.module.css";
import styles from "./styles.module.css";
import ReportCardIcon from "./ReportCardIcon";
import ReportCardPagination from "./ReportCardPagination";
import useReportCardRequest from "./useReportCardRequest";
import ReportCardObjection from "./ReportCardObjection";

const posterUrl = poster => /^https?:\/\//i.test(poster) ? poster
  : `${baseURL.replace(/\/$/, "")}/file/get/${encodeURIComponent(poster)}`;

function CollapsibleCard({ id, title, expanded, onToggle, children }) {
  return <section className={styles.collapsibleCard} aria-labelledby={`${id}-title`}>
    <h2 id={`${id}-title`} className={styles.cardHeading}>
      <button type="button" className={styles.cardToggle} aria-expanded={expanded} aria-controls={`${id}-content`} onClick={onToggle}>
        <span>{title}</span>
        <span className={styles.cardChevron} aria-hidden="true" />
      </button>
    </h2>
    <div id={`${id}-content`} className={styles.cardContent} hidden={!expanded}>{children}</div>
  </section>;
}

export default function ReportCardDetails() {
  const { id } = useParams();
  return <Details key={id} id={id} />;
}

function Details({ id }) {
  const [page, setPage] = useState(1);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const [answersExpanded, setAnswersExpanded] = useState(false);
  const { data, loading, error, retry } = useReportCardRequest(`/reportcard/getuserbyid/${id}`, page);
  const card = data?.reportCard;
  return (
    <AnimatedPageContainer>
      <div className="customContainer" dir="rtl">
        {loading ? <div className="w-100 d-flex justify-content-center py-5 my-5" role="status" aria-label="در حال دریافت کارنامه"><Loading size={30} /></div>
          : error ? <div className={styles.status} role="alert"><p>{error}</p><button className={styles.button} onClick={retry}>تلاش مجدد</button></div>
          : card && <>
            <div className="w-100 d-flex justify-content-center pt-5 pb-3"><ReportCardIcon icon={card.icon} /></div>
            <h1 className={`${formStyles.title} ${styles.heading}`}>{card.title}</h1>
            {card.subtitle && <p className={formStyles.formSubtitle}>{card.subtitle}</p>}
            {card.poster && <img src={posterUrl(card.poster)} alt={card.title} className="w-100 img-fluid my-4 borderRadius" />}
            {card.description && <div className={formStyles.formDesc}><p className={styles.description}>{card.description}</p></div>}
            <CollapsibleCard id="report-results" title="نتایج شما" expanded={resultsExpanded} onToggle={() => setResultsExpanded(value => !value)}>
            {data.results.docs.map((result, index) => (
              <section key={result._id} className={styles.resultSection} aria-label={`نتیجه ${(page - 1) * 10 + index + 1}`}>
                <table className={styles.resultTable} aria-label={`نتیجه ${(page - 1) * 10 + index + 1}`}>
                  <thead><tr><th scope="col">عنوان</th><th scope="col">نتیجه</th></tr></thead>
                  <tbody>
                    {result.transcripts.length ? result.transcripts.map((item, rowIndex) => (
                      <tr key={item._id || rowIndex}>
                        <th scope="row">{item.title}</th><td>{item.result}</td>
                      </tr>
                    )) : <tr><td colSpan={2} className={styles.emptyTranscript}>هنوز جزئیاتی برای این نتیجه ثبت نشده است.</td></tr>}
                  </tbody>
                </table>
              </section>
            ))}
            {!data.results.docs.length && <NoResultAlter className="py-5" text="نتیجه‌ای در این صفحه وجود ندارد." />}
            <ReportCardPagination page={page} totalPages={data.results.totalPages} onChange={setPage} />
            </CollapsibleCard>
            {card.hasForm && card.form && <CollapsibleCard id="form-answers" title="پاسخ‌های آزمون" expanded={answersExpanded} onToggle={() => setAnswersExpanded(value => !value)}>
              {data.formResponse?.fields?.length ? <table className={`${styles.resultTable} ${styles.answersTable}`} aria-label="پاسخ‌های فرم مرتبط">
                <tbody>{data.formResponse.fields.map((field, index) => (
                  <React.Fragment key={field._id || field.formFieldId || index}>
                    <tr><th id={`form-answer-${index}`}>{field.title}</th></tr>
                    <tr><td headers={`form-answer-${index}`}>
                      <div className={styles.answerLine}><strong>پاسخ شما: </strong><span>{field.userInput === "" ? "—" : field.userInput ?? "—"}</span></div>
                      {field.excludedFromScoring && <div className={styles.answerLine}>حذف از آزمون</div>}
                      {field.scoringPolicy === 'creditAll' && <div className={styles.answerLine}>پاسخ صحیح برای همه</div>}
                      {field.rightAnswerWasInInput !== true && field.rightAnswer != null && String(field.rightAnswer).trim() !== "" &&
                        <div className={styles.answerLine}><strong>پاسخ صحیح: </strong><span>{field.rightAnswer}</span></div>}
                      {typeof field.rightAnswerWasInInput === "boolean" &&
                        <div className={styles.answerLine}><strong>وضعیت پاسخ: </strong>
                          <span className={field.rightAnswerWasInInput ? styles.correctAnswer : styles.incorrectAnswer}>{field.rightAnswerWasInInput ? "صحیح" : "غلط"}</span>
                        </div>}
                    </td></tr>
                  </React.Fragment>
                ))}</tbody>
              </table> : <p className={styles.emptyFormAnswers}>پاسخی برای فرم مرتبط ثبت نشده است.</p>}
            </CollapsibleCard>}
            <ReportCardObjection card={card} objection={data.objection} canObject={data.canObject} onRefresh={retry} />
          </>}
      </div>
    </AnimatedPageContainer>
  );
}
